import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Pagination,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";

import ActionsMenu, { type ActionsMenuItem } from "components/botsManagement/ActionsMenu";
import BotsRecordTable, { type BotsColumn } from "components/botsManagement/BotsRecordTable";
import SoftTag from "components/botsManagement/SoftTag";
import StatusChip from "components/botsManagement/StatusChip";
import {
  createCoalescer,
  splitEventConcerns,
} from "components/botsManagement/panelWsEvents";
import {
  useGetPdfJobsQuery,
  useLazyGetPdfJobDownloadQuery,
  type PdfJobListItem,
} from "features/pdfTools/pdfTools";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch, useAppSelector } from "features/store";
import { botsManagementWs } from "features/ws/botsManagementWs";
import useAppNavigate from "hooks/useAppNavigate";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import NewSplitJobDialog from "./NewSplitJobDialog";
import SplitJobDeleteDialog from "./SplitJobDeleteDialog";
import {
  apiErrorText,
  formatSplitDateTime,
  formatSplitInputs,
  isSplitJobActive,
  isSplitJobSaving,
  splitListNeedsPolling,
  splitProgressSummary,
  splitResultTag,
  splitStatusChip,
} from "./splitJobUi";
import { splitJobPath } from "./splitRoutes";
import { deriveSplitSsnState, splitSsnListTag } from "./splitSsn";

const PAGE_SIZE = 25;
/** Polled only while the socket is down and a row is still moving. */
const FALLBACK_POLL_MS = 4000;

export interface SplitJobsTabProps {
  userId: number;
  userLabel: string;
  majorJob: { id: number; name: string; vendor: string };
  canEdit: boolean;
  readOnlyReason: string | null;
}

function ProgressCell({ job }: { job: PdfJobListItem }) {
  const progress = splitProgressSummary(job);
  const active = isSplitJobActive(job);
  return (
    <Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
      <Typography
        variant="caption"
        fontWeight={600}
        noWrap
        title={progress.primary}
        sx={{ display: "block", lineHeight: 1.35, fontVariantNumeric: "tabular-nums" }}
      >
        {progress.primary}
      </Typography>
      <LinearProgress
        variant={active && progress.percent == null ? "indeterminate" : "determinate"}
        value={progress.percent ?? 0}
        color={
          job.status === "completed"
            ? "success"
            : job.status === "failed"
              ? "error"
              : job.status === "stopped" || job.status === "canceled"
                ? "warning"
                : "primary"
        }
        sx={{ height: 4, borderRadius: 2, my: 0.25 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={progress.secondary || undefined}
        sx={{ display: "block", lineHeight: 1.35, minHeight: "1.35em" }}
      >
        {progress.secondary}
      </Typography>
    </Box>
  );
}

function ResultTags({ job, card = false }: { job: PdfJobListItem; card?: boolean }) {
  const result = splitResultTag(job);
  const ssn = splitSsnListTag(deriveSplitSsnState(job), job.ssn_check);
  if (!result && !ssn) {
    // A card's badge row simply has nothing more to show.
    return card ? null : (
      <Typography variant="caption" color="text.secondary">
        -
      </Typography>
    );
  }
  return (
    <Stack direction="row" useFlexGap sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
      {result && <SoftTag tone={result.tone} label={result.label} />}
      {ssn && (
        <SoftTag
          tone={ssn.tone}
          label={ssn.label}
          title="The saved folders against the vendor's SSN file"
        />
      )}
    </Stack>
  );
}

/**
 * The Split jobs tab of a split major job (CAL-6001): the vendor's PDFs split
 * into employee folders, each job a row with its status, result and SSN state.
 * Updates arrive over the panel's socket; the list polls only while it is down.
 */
export default function SplitJobsTab({
  userId,
  userLabel,
  majorJob,
  canEdit,
  readOnlyReason,
}: SplitJobsTabProps) {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const [page, setPage] = useState(1);
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PdfJobListItem | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const wsAccessToken = useAppSelector((state) => state.auth.accessToken);
  const [wsConnected, setWsConnected] = useState(() => botsManagementWs.isConnected());
  const [pollMs, setPollMs] = useState(0);
  const listArgs = { user_id: userId, major_job_id: majorJob.id, page, page_size: PAGE_SIZE };
  const { data, isLoading, error, refetch } = useGetPdfJobsQuery(listArgs, {
    pollingInterval: pollMs,
    refetchOnMountOrArgChange: true,
  });
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const [triggerDownload] = useLazyGetPdfJobDownloadQuery();

  const rows = useMemo(() => data?.results ?? [], [data]);
  const total = data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // A delete can empty the last page: the server answers 404 for it and the
  // cached count still holds the old total, so go back to the first page.
  const errorStatus = (error as { status?: unknown } | undefined)?.status;
  const pageGone = errorStatus === 404 && page > 1;
  useEffect(() => {
    if (pageGone) setPage(1);
    else if (page > pageCount) setPage(pageCount);
  }, [pageGone, page, pageCount]);

  useEffect(() => {
    const token = wsAccessToken || localStorage.getItem("accessToken");
    if (token) botsManagementWs.connect(token);
    const unStatus = botsManagementWs.subscribeStatus((connected) => {
      setWsConnected(connected);
      // What happened while the socket was down is only in the database.
      if (connected) void refetchRef.current();
    });
    setWsConnected(botsManagementWs.isConnected());
    const coalescer = createCoalescer();
    const unEvents = botsManagementWs.subscribe((event) => {
      if (!splitEventConcerns(event, { userId, majorJobId: majorJob.id })) return;
      coalescer.schedule("split-jobs", () => void refetchRef.current(), 700);
    });
    return () => {
      unStatus();
      unEvents();
      coalescer.cancelAll();
    };
  }, [userId, majorJob.id, wsAccessToken]);

  const moving = splitListNeedsPolling(rows);
  useEffect(() => {
    setPollMs(!wsConnected && moving ? FALLBACK_POLL_MS : 0);
  }, [wsConnected, moving]);

  const openJob = (job: PdfJobListItem) => navigate(splitJobPath(userId, majorJob.id, job.job_id));

  const download = async (job: PdfJobListItem) => {
    setDownloadingId(job.job_id);
    try {
      const archive = await triggerDownload(job.job_id).unwrap();
      startDownloadViaHiddenFrame(archive.download_url);
    } catch (err: unknown) {
      dispatch(
        pushNotification({
          message: apiErrorText(err, "The download could not start. Try again."),
          severity: "error",
        }),
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const rowActions = (job: PdfJobListItem): ActionsMenuItem[] => {
    const busy = isSplitJobActive(job) || isSplitJobSaving(job);
    return [
      {
        key: "download",
        label: "Download ZIP",
        icon: <DownloadIcon fontSize="small" />,
        disabled: !job.has_results_zip || downloadingId === job.job_id,
        hint: job.has_results_zip ? undefined : "The results archive is not ready yet.",
        onClick: () => void download(job),
      },
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteIcon fontSize="small" />,
        danger: true,
        separatorBefore: true,
        disabled: !canEdit || busy,
        hint:
          readOnlyReason ??
          (busy ? "Stop the job or wait for its save to finish first." : undefined),
        onClick: () => setDeleteTarget(job),
      },
    ];
  };

  const columns: BotsColumn<PdfJobListItem>[] = [
    {
      key: "job",
      header: "Split job",
      align: "left",
      card: "title",
      cell: (job) => {
        const inputs = formatSplitInputs(job.input_names, job.files_count);
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap title={job.splitter_label}>
              {job.splitter_label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={job.input_names.join(", ") || undefined}
              sx={{ display: "block", minHeight: "1.66em" }}
            >
              {inputs || "-"}
            </Typography>
          </Box>
        );
      },
      cardCell: (job) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
            {job.splitter_label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", overflowWrap: "anywhere", fontWeight: 400 }}
          >
            {formatSplitInputs(job.input_names, job.files_count) || "-"}
          </Typography>
        </Box>
      ),
      skeleton: () => (
        <Box>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: 130,
      compactWidth: 116,
      card: "badge",
      cell: (job) => {
        const chip = splitStatusChip(job.status);
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <StatusChip label={chip.label} color={chip.color} />
          </Box>
        );
      },
      cardCell: (job) => {
        const chip = splitStatusChip(job.status);
        return <StatusChip label={chip.label} color={chip.color} />;
      },
      skeleton: () => <Skeleton variant="rounded" width={84} height={22} sx={{ mx: "auto" }} />,
    },
    {
      key: "result",
      header: "Result",
      width: 190,
      compactWidth: 160,
      align: "left",
      card: "badge",
      cell: (job) => <ResultTags job={job} />,
      cardCell: (job) => <ResultTags job={job} card />,
      skeleton: () => <Skeleton variant="rounded" width={90} height={22} />,
    },
    {
      key: "progress",
      header: "Progress",
      width: 220,
      compactWidth: 180,
      align: "left",
      // A card shows the bar full width under its fields instead.
      card: "hidden",
      cell: (job) => <ProgressCell job={job} />,
      skeleton: () => (
        <Box>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="rounded" height={4} sx={{ my: 0.25 }} />
          <Skeleton variant="text" width="40%" />
        </Box>
      ),
    },
    {
      key: "progressCard",
      header: "",
      card: "block",
      cardOnly: true,
      cell: () => null,
      cardCell: (job) => <ProgressCell job={job} />,
    },
    {
      key: "created",
      header: "Created",
      width: 170,
      // A laptop with the users panel open needs the room; the job page says it.
      compactHidden: true,
      align: "left",
      cell: (job) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            noWrap
            title={formatSplitDateTime(job.created_at)}
            sx={{ display: "block", fontVariantNumeric: "tabular-nums" }}
          >
            {formatSplitDateTime(job.created_at, { short: true })}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={job.created_by.username || undefined}
            sx={{ display: "block", minHeight: "1.66em" }}
          >
            {job.created_by.username ? `by ${job.created_by.username}` : ""}
          </Typography>
        </Box>
      ),
      cardCell: (job) =>
        `${formatSplitDateTime(job.created_at)}${
          job.created_by.username ? ` by ${job.created_by.username}` : ""
        }`,
      skeleton: () => (
        <Box>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="50%" />
        </Box>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: 128,
      card: "actions",
      cell: (job) => (
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="center"
          alignItems="center"
          onClick={(event) => event.stopPropagation()}
        >
          <Button size="small" onClick={() => openJob(job)}>
            Open
          </Button>
          <ActionsMenu actions={rowActions(job)} ariaLabel="More split job actions" />
        </Stack>
      ),
      skeleton: () => <Skeleton variant="rounded" width={88} height={28} sx={{ mx: "auto" }} />,
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Split jobs
          {total > 0 && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 0.75, fontVariantNumeric: "tabular-nums" }}
            >
              {total.toLocaleString()}
            </Typography>
          )}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            ml: "auto",
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": {
              flex: { xs: 1, sm: "0 0 auto" },
              minHeight: { xs: 38, sm: 0 },
              whiteSpace: "nowrap",
              px: { xs: 1, sm: 2 },
            },
          }}
        >
          <Tooltip title={readOnlyReason ?? ""}>
            <span style={{ display: "flex", flex: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => setNewOpen(true)}
                disabled={!canEdit}
              >
                New split job
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error != null && !pageGone && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {apiErrorText(error, "The split jobs could not be loaded.")}
        </Alert>
      )}

      <BotsRecordTable<PdfJobListItem>
        rows={rows}
        rowKey={(job) => job.job_id}
        columns={columns}
        loading={isLoading}
        skeletonRows={3}
        onRowClick={openJob}
        empty={
          <Typography color="text.secondary">
            No split jobs yet. New split job uploads the client&apos;s {majorJob.vendor} PDFs
            (or a ZIP of them) and splits them into one folder per employee.
          </Typography>
        }
      />

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(_event, next: number) => setPage(next)}
          />
        </Box>
      )}

      <NewSplitJobDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        userId={userId}
        userLabel={userLabel}
        majorJob={majorJob}
        readOnlyReason={readOnlyReason}
      />
      <SplitJobDeleteDialog job={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </Box>
  );
}
